import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, FilePlus2 } from "@jurisui/core/icons";
import { Button } from "@jurisui/core/primitives/button";
import {
  SvgDiagramCanvas,
  DiagramAnnotationDetails,
  type DiagramDefinition,
} from "@jurisui/studio-ui/components/diagrams";
import { type DiagramRecipe } from "../diagram-types";
import "../architecture.css";

export interface RecipeDiagramProps {
  sourceLabels?: Record<string, string>;
  diagram: DiagramRecipe;
  onExplore?: (diagram: DiagramRecipe) => void;
  onSave?: (diagram: DiagramRecipe) => void | Promise<void>;
  compact?: boolean;
  selectedEntityIds?: string[];
  highlightedEntityIds?: string[];
  selectedEdgeId?: string;
  onNodeSelect?: (id: string, options: { additive: boolean }) => void;
  onEdgeSelect?: (id: string) => void;
}

export function RecipeDiagram({
  sourceLabels,
  diagram,
  onExplore,
  onSave,
  compact = false,
  selectedEntityIds = [],
  highlightedEntityIds = [],
  selectedEdgeId,
  onNodeSelect,
  onEdgeSelect,
}: RecipeDiagramProps) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [direction, setDirection] = useState(diagram.direction);
  const [inspected, setInspected] = useState<{ type: 'node' | 'edge' | 'boundary'; id: string }>();
  useEffect(() => { setInspected(undefined); }, [diagram.id]);
  useEffect(() => {
    setDirection(diagram.direction);
  }, [diagram.id, diagram.direction]);
  const displayedRecipe = useMemo(
    () => ({ ...diagram, direction }),
    [diagram, direction],
  );
  const technical = diagram.displayLevel === 'technical';
  const inspectedItem = inspected?.type === 'boundary' ? diagram.boundaries?.find(boundary => boundary.id === inspected.id) : inspected?.type === 'edge' ? diagram.edges.find(edge => edge.id === inspected.id)
    : diagram.nodes.find(node => node.id === inspected?.id) ?? diagram.nodes[0];
  const definition = useMemo<DiagramDefinition>(
    () => ({
      id: diagram.id,
      title: diagram.title,
      description: diagram.description,
      kind: diagram.kind,
      mode: diagram.mode,
      ...(technical ? { annotationLimit: { maxFacts: 3, maxLinesPerFact: 6 } } : {}),
      nodes: diagram.nodes,
      edges: diagram.edges,
      boundaries: diagram.boundaries,
      assumptions: diagram.assumptions,
      direction,
      provenance: {
        label: diagram.catalog.catalogId,
        revision: diagram.catalog.revision,
        digest: diagram.catalog.sha256,
        phase: diagram.catalog.phase,
      },
      metadata: {
        schemaVersion: diagram.schemaVersion,
        recipe: displayedRecipe,
      },
    }),
    [diagram, direction, displayedRecipe, technical],
  );
  const selectedNodeIds = diagram.nodes
    .filter((node) => selectedEntityIds.includes(node.entityId ?? node.id))
    .map((node) => node.id);
  const highlightedNodeIds = diagram.nodes
    .filter((node) => highlightedEntityIds.includes(node.entityId ?? node.id))
    .map((node) => node.id);

  async function save() {
    setSaveError("");
    setSaving(true);
    try {
      await onSave?.(displayedRecipe);
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : "The diagram could not be kept in the document.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <section
      className={`recipe-diagram ${compact ? "is-compact" : ""}`}
      data-testid="recipe-diagram"
      data-recipe-id={diagram.id}
    >
      {(onExplore || onSave) && (
        <div className="recipe-diagram-context">
          <div>
            {onExplore && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onExplore(displayedRecipe)}
              >
                Explore
                <ArrowUpRight aria-hidden="true" />
              </Button>
            )}
            {onSave && (
              <Button
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={() => void save()}
              >
                <FilePlus2 aria-hidden="true" />
                {saving ? "Adding…" : "Keep in document"}
              </Button>
            )}
          </div>
        </div>
      )}
      {saveError && (
        <p className="architecture-export-error" role="alert">
          {saveError}
        </p>
      )}
      <SvgDiagramCanvas
        minimumAutoZoom={0.85}
        diagram={definition}
        height={compact ? 380 : undefined}
        selectedNodeIds={selectedNodeIds}
        highlightedNodeIds={highlightedNodeIds}
        selectedEdgeId={selectedEdgeId}
        onNodeSelect={technical ? (id, options) => { setInspected({ type: 'node', id }); onNodeSelect?.(id, options); } : onNodeSelect}
        onEdgeSelect={technical ? id => { setInspected({ type: 'edge', id }); onEdgeSelect?.(id); } : onEdgeSelect}
        onBoundarySelect={technical ? id => setInspected({ type: "boundary", id }) : undefined}
        onDirectionChange={setDirection}
      />
      {technical && <div role="region" className="technical-inspector" aria-label="Technical details inspector">
        <label>Inspect recorded component or connection <select aria-label="Technical detail selection" value={inspected ? `${inspected.type}:${inspected.id}` : `node:${diagram.nodes[0]?.id ?? ''}`} onChange={event => {
          const separator = event.target.value.indexOf(':'); setInspected({ type: event.target.value.slice(0, separator) as 'node' | 'edge' | 'boundary', id: event.target.value.slice(separator + 1) });
        }}>
          <optgroup label="Components">{diagram.nodes.map(node => <option key={node.id} value={`node:${node.id}`}>{node.label}</option>)}</optgroup>
          <optgroup label="Boundaries">{diagram.boundaries?.map(boundary => <option key={boundary.id} value={`boundary:${boundary.id}`}>{boundary.label}</option>)}</optgroup>
          <optgroup label="Connections">{diagram.edges.map(edge => <option key={edge.id} value={`edge:${edge.id}`}>{edge.label}</option>)}</optgroup>
        </select></label>
        <DiagramAnnotationDetails title={inspectedItem?.label ?? 'Recorded technical details'} annotations={inspectedItem?.annotations ?? []} sourceLabels={sourceLabels} />
        <p>Source status and observation dates describe recorded evidence, not a live reachability test.</p>
        <p>The canvas shows up to three concise facts per item. Additional and longer recorded facts are shown completely in this inspector.</p>
      </div>}
    </section>
  );
}
